import { PageLayout, Section } from "@/components/layout";
import { useClientTranslation } from "@/hooks/use-client-translation";
import {
  Plug,
  Sparkles,
  BarChart3,
  ShieldCheck,
  Handshake,
  Code2,
  Workflow,
  Zap,
  Check,
  ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

const CARD_ICONS: LucideIcon[] = [Plug, Sparkles, BarChart3, ShieldCheck];
const STEP_ICONS: LucideIcon[] = [Handshake, Code2, Workflow, Zap];

function contactPartners() {
  window.open("mailto:partners@mezepay.com", "_self");
}

/* -------------------------------------------------------------------------- */
/*                                  Hero                                      */
/* -------------------------------------------------------------------------- */

interface Stat {
  value: string;
  label: string;
}

function HeroSection() {
  const { t } = useClientTranslation();
  const stats = t("integrations.hero.stats", {
    returnObjects: true,
  }) as Stat[];

  return (
    <section className="relative w-full overflow-hidden bg-purple-primary">
      {/* Decorative glows */}
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-32 left-1/3 h-96 w-96 rounded-full opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)",
        }}
      />

      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="relative mx-auto max-w-[1280px] py-14 md:py-20">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
              <Plug className="h-3.5 w-3.5" strokeWidth={2} />
              {t("integrations.hero.eyebrow")}
            </span>
            <h1 className="mt-5 max-w-[880px] text-[32px] font-medium leading-[105%] tracking-[-0.02em] text-white md:text-5xl lg:text-[60px]">
              {t("integrations.hero.title")}
            </h1>
            <p className="mt-6 max-w-[680px] text-[17px] font-normal leading-[155%] text-white/85 md:text-[19px]">
              {t("integrations.hero.body")}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={contactPartners}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-medium text-black transition-opacity hover:opacity-90"
              >
                {t("integrations.hero.primaryCta")}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-2 gap-8 border-t border-white/15 pt-10 md:grid-cols-4">
              {stats.map((stat) => (
                <div key={stat.label} className="flex flex-col gap-1">
                  <span className="text-3xl font-medium tracking-[-0.02em] text-white md:text-4xl">
                    {stat.value}
                  </span>
                  <span className="text-sm font-normal leading-snug text-white/70">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Trusted-by strip                              */
/* -------------------------------------------------------------------------- */

// Partner logo images (in /public). Keyed by the logo name from i18n.
const LOGO_IMAGES: Record<string, string> = {
  Spotlight: "/spotlight.svg",
  WLabel: "/w-label.png",
};

function TrustedBy() {
  const { t } = useClientTranslation();
  const logos = t("integrations.trustedBy.logos", {
    returnObjects: true,
  }) as string[];

  return (
    <Section className="px-0 py-10 md:py-14" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-7">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
            {t("integrations.trustedBy.heading")}
          </span>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {logos.map((logo) => (
              <div
                key={logo}
                className="flex items-center gap-2.5 rounded-xl border border-[rgba(0,0,0,0.08)] bg-white px-5 py-3 shadow-sm"
              >
                {LOGO_IMAGES[logo] && (
                  <img
                    src={LOGO_IMAGES[logo]}
                    alt={logo}
                    className="h-6 w-6 shrink-0 object-contain"
                  />
                )}
                <span className="text-[15px] font-medium tracking-tight text-foreground/75">
                  {logo}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Feature cards section                            */
/* -------------------------------------------------------------------------- */

interface FeatureCard {
  title: string;
  body: string;
  checklist: string[];
}

function UnlocksSection() {
  const { t } = useClientTranslation();
  const cards = t("integrations.unlocks.cards", {
    returnObjects: true,
  }) as FeatureCard[];

  return (
    <Section className="px-0 border-t border-[rgba(0,0,0,0.08)] py-16 md:py-24" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto max-w-[1280px]">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
            {t("integrations.unlocks.eyebrow")}
          </span>
          <h2 className="mt-4 max-w-[760px] text-[30px] font-medium leading-[110%] tracking-[-0.02em] md:text-[44px]">
            {t("integrations.unlocks.title")}
          </h2>
          <p className="mt-5 max-w-[680px] text-base font-normal leading-[160%] text-foreground/65">
            {t("integrations.unlocks.intro")}
          </p>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {cards.map((card, i) => {
              const Icon = CARD_ICONS[i] ?? Plug;
              return (
                <div
                  key={card.title}
                  className="flex flex-col rounded-2xl border border-[rgba(0,0,0,0.1)] bg-white p-7"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-primary">
                    <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                  </div>
                  <h3 className="mt-5 text-[22px] font-semibold leading-snug tracking-[-0.01em]">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-[15px] font-normal leading-[160%] text-foreground/65">
                    {card.body}
                  </p>
                  <ul className="mt-6 flex flex-col gap-3 border-t border-[rgba(0,0,0,0.08)] pt-6">
                    {card.checklist.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-foreground"
                          strokeWidth={2.5}
                        />
                        <span className="text-sm font-normal leading-snug text-foreground/80">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Process / steps                               */
/* -------------------------------------------------------------------------- */

interface Step {
  number: string;
  title: string;
  description: string;
}

function ProcessSection() {
  const { t } = useClientTranslation();
  const steps = t("integrations.process.steps", {
    returnObjects: true,
  }) as Step[];

  return (
    <Section className="px-0 border-t border-[rgba(0,0,0,0.08)] py-16 md:py-24" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto max-w-[1280px]">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
            {t("integrations.process.eyebrow")}
          </span>
          <h2 className="mt-4 max-w-[820px] text-[28px] font-medium leading-[115%] tracking-[-0.02em] md:text-[44px]">
            {t("integrations.process.title")}
          </h2>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, i) => {
              const Icon = STEP_ICONS[i] ?? Handshake;
              return (
                <div
                  key={step.number}
                  className="flex flex-col rounded-2xl border border-[rgba(0,0,0,0.08)] p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-semibold uppercase tracking-[0.08em] text-foreground/40">
                      {step.number}
                    </span>
                    <Icon className="h-5 w-5 text-foreground/40" strokeWidth={2} />
                  </div>
                  <h3 className="mt-9 text-lg font-semibold leading-snug tracking-[-0.005em]">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm font-normal leading-[155%] text-foreground/65">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Testimonial                                  */
/* -------------------------------------------------------------------------- */

function TestimonialSection() {
  const { t } = useClientTranslation();

  return (
    <Section className="px-0 border-t border-[rgba(0,0,0,0.08)] py-16 md:py-24" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto flex max-w-[960px] flex-col items-center gap-8 text-center">
          <blockquote className="text-2xl font-medium leading-[135%] tracking-[-0.01em] md:text-[30px]">
            {t("integrations.testimonial.quote")}
          </blockquote>
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-primary text-sm font-semibold text-white">
              {t("integrations.testimonial.initials")}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[15px] font-semibold leading-snug">
                {t("integrations.testimonial.name")}
              </span>
              <span className="text-[13px] font-normal leading-snug text-foreground/55">
                {t("integrations.testimonial.role")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Dark CTA band                                */
/* -------------------------------------------------------------------------- */

function CtaSection() {
  const { t } = useClientTranslation();

  return (
    <Section className="px-0 py-12 md:py-20" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto max-w-[1280px]">
          <div className="flex flex-col items-center gap-7 rounded-2xl bg-[#0E0B14] px-6 py-16 text-center md:rounded-[40px] md:px-16 md:py-20">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
              {t("integrations.cta.eyebrow")}
            </span>
            <h2 className="max-w-[760px] text-[28px] font-medium leading-[120%] tracking-[-0.015em] text-white md:text-[44px]">
              {t("integrations.cta.title")}
            </h2>
            <p className="max-w-[600px] text-[17px] font-normal leading-[160%] text-white/85">
              {t("integrations.cta.body")}
            </p>
            <button
              type="button"
              onClick={contactPartners}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-[15px] font-medium text-black transition-opacity hover:opacity-90"
            >
              {t("integrations.cta.button")}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Integrations Page                              */
/* -------------------------------------------------------------------------- */

export default function IntegrationsPage() {
  return (
    <PageLayout activePage="integrations">
      <main className="flex-1">
        <HeroSection />
        <TrustedBy />
        <UnlocksSection />
        <ProcessSection />
        <TestimonialSection />
        <CtaSection />
      </main>
    </PageLayout>
  );
}
